/**
 * The live mission line, plus any route we replaced.
 *
 * The planned layer in the intel map is one colour. A run needs two readings:
 * how far the lead vehicle has already driven (grey, faded) and the line still
 * ahead (blue). A reroute keeps the old geometry underneath so the operator
 * can still see where the convoy left the original plan.
 */
import React, { useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import {
  TRAIL_GHOST,
  TRAIL_LIVE,
  TRAIL_TRAVELED,
  ghostLines,
  splitTrail,
  trailCutRatio,
} from './missionRouteTrail';

const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const ROUTE_LAYER_IDS = [
  'mission-route-ghost-line',
  'mission-route-traveled-casing',
  'mission-route-traveled-line',
  'mission-route-live-casing',
  'mission-route-live-line',
];

export interface MissionRouteLayerProps {
  geometry?: GeoJSON.LineString | null;
  alternate?: GeoJSON.LineString | null;
  prior?: GeoJSON.LineString | null;
  progress?: number | null;
  active?: boolean;
  /** Lead vehicle, used so the fade meets the pin when they are on the line. */
  lead?: { longitude: number; latitude: number } | null;
  /**
   * Existing layer to slot the lines beneath. The host's waypoint dots mount
   * before this layer does, so without it the route paints over them.
   */
  beforeId?: string;
}

function collection(line: GeoJSON.LineString | null): GeoJSON.FeatureCollection {
  if (!line || (line.coordinates?.length || 0) < 2) return empty;
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: {}, geometry: line }],
  };
}

const MissionRouteLayer: React.FC<MissionRouteLayerProps> = ({
  geometry,
  alternate,
  prior,
  progress,
  lead,
  beforeId,
}) => {
  const { current: mapRef } = useMap();
  const ratio = geometry
    ? trailCutRatio(geometry, progress ?? 0, lead)
    : 0;
  const parts = useMemo(
    () => (geometry && (geometry.coordinates?.length || 0) >= 2
      ? splitTrail(geometry, ratio)
      : { traveled: null, remaining: null }),
    [geometry, ratio],
  );
  const traveled = useMemo(() => collection(parts.traveled), [parts.traveled]);
  const remaining = useMemo(() => collection(parts.remaining), [parts.remaining]);

  const ghosts = useMemo<GeoJSON.FeatureCollection>(() => ({
    type: 'FeatureCollection',
    features: ghostLines(geometry, prior, alternate).map((line, index) => ({
      type: 'Feature' as const,
      id: index,
      properties: {},
      geometry: line,
    })),
  }), [geometry, prior, alternate]);

  // beforeId is ignored when the host layer is not on the style yet
  // (reuseMaps, late mount). Push the stroke under the first pin layer
  // once it exists so a refresh cannot leave the line on top.
  useEffect(() => {
    const map = mapRef?.getMap?.();
    if (!map) return undefined;
    const sink = () => {
      const before = beforeId && map.getLayer(beforeId)
        ? beforeId
        : ['intel-point-glow', 'intel-points', 'intel-point-ring'].find((id) => map.getLayer(id));
      if (!before) return;
      ROUTE_LAYER_IDS.forEach((id) => {
        if (!map.getLayer(id)) return;
        try {
          map.moveLayer(id, before);
        } catch {
          // Style swap mid-pass.
        }
      });
    };
    sink();
    map.on('styledata', sink);
    return () => {
      map.off('styledata', sink);
    };
  }, [mapRef, beforeId, geometry, prior, alternate, progress]);

  if (traveled.features.length === 0 && remaining.features.length === 0 && ghosts.features.length === 0) {
    return null;
  }

  return (
    <>
      {ghosts.features.length > 0 && (
        <Source id="mission-route-ghost" type="geojson" data={ghosts}>
          <Layer
            id="mission-route-ghost-line"
            beforeId={beforeId}
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{
              'line-color': TRAIL_GHOST,
              'line-width': 4,
              'line-opacity': 0.85,
              'line-dasharray': [1.6, 1.4],
            }}
          />
        </Source>
      )}
      {traveled.features.length > 0 && (
        <Source id="mission-route-traveled" type="geojson" data={traveled}>
          <Layer
            id="mission-route-traveled-casing"
            beforeId={beforeId}
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': '#0b1017', 'line-width': 6, 'line-opacity': 0.35 }}
          />
          <Layer
            id="mission-route-traveled-line"
            beforeId={beforeId}
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': TRAIL_TRAVELED, 'line-width': 3.5, 'line-opacity': 0.88 }}
          />
        </Source>
      )}
      {remaining.features.length > 0 && (
        <Source id="mission-route-live" type="geojson" data={remaining}>
          <Layer
            id="mission-route-live-casing"
            beforeId={beforeId}
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': '#0b1017', 'line-width': 7, 'line-opacity': 0.55 }}
          />
          <Layer
            id="mission-route-live-line"
            beforeId={beforeId}
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{ 'line-color': TRAIL_LIVE, 'line-width': 4.5 }}
          />
        </Source>
      )}
    </>
  );
};

export default MissionRouteLayer;
